import { type ChangeEvent, Component, type SubmitEvent } from "react";
import {
	getCategorizationOptions,
	startConversation,
} from "../../api/secure-messaging-api";
import SSNField from "../../components/ssn-field";
import ThreeDotsLoadingSpinner from "../../components/three-dots-loading-spinner";
import culture from "../../constants/culture-codes";
import events from "../../constants/events";
import link from "../../constants/links";
import messageCategory from "../../constants/message-category";
import translate from "../../constants/translations";
import hideAttachments from "../../helpers/attachment-helper";
import { showSSNField, ssnIsValid } from "../../helpers/ssn-helper";
import type { CategorizationOption, CustomerContext } from "../../types";
import FileAttachmentDropDown from "../file-attachments/file-attachment-dropdown";
import type { UploadItem } from "../file-attachments/upload-progress-bar";

const selectMessageCategoryPrompt = "SelectMessageCategory";
const dropdownCategoriesToHide: string[] = [];

export type ConversationCategory = string;
export interface ClaimInfo {
	id: string;
}

interface StartConversationProps {
	customer: CustomerContext;
	cultureCode: string;
	sendAllowed: boolean;
	category?: string;
}

interface StartConversationState {
	categorizationOptions: CategorizationOption[];
	category: string;
	body: string;
	subject: string;
	ssn: string;
	attachments: unknown[];
	uploads: UploadItem[];
	busy: boolean;
	showCategoryDropdown: boolean;
	loading: boolean;
}

class StartConversation extends Component<
	StartConversationProps,
	StartConversationState
> {
	private mounted = false;

	constructor(props: StartConversationProps) {
		super(props);

		this.onInputChange = this.onInputChange.bind(this);
		this.changeCategory = this.changeCategory.bind(this);
		this.onSendMessageClick = this.onSendMessageClick.bind(this);
		this.onCancelClick = this.onCancelClick.bind(this);
		this.hasActiveAttachmentUploads =
			this.hasActiveAttachmentUploads.bind(this);
		this.updateUploads = this.updateUploads.bind(this);
		this.updateAttachments = this.updateAttachments.bind(this);

		this.state = {
			categorizationOptions: [],
			category: selectMessageCategoryPrompt,
			body: "",
			subject: "",
			ssn: "",
			attachments: [],
			uploads: [],
			busy: false,
			showCategoryDropdown: true,
			loading: true,
		};
	}

	componentDidMount() {
		this.mounted = true;
		window.dispatchEvent(new Event(events.newMessageStarted));
		this.setCategoryDropdown();
	}

	componentWillUnmount() {
		this.mounted = false;
	}

	private t(term: string): string {
		return translate(this.props.cultureCode, term);
	}

	private l(id: string): string {
		return link(this.props.cultureCode, id);
	}

	private setCategoryDropdown() {
		getCategorizationOptions(this.props.customer).then((options) => {
			if (this.mounted && options) {
				const isCategoryPreSelected = this.isCategoryPreSelected(
					options,
					this.props.category,
				);
				this.setState({
					categorizationOptions: options,
					category: this.categoryFromProps(
						isCategoryPreSelected,
						this.props.category,
						selectMessageCategoryPrompt,
					),
					showCategoryDropdown: this.showCategoryDropdown(
						isCategoryPreSelected,
					),
					loading: false,
				});
			}
		});
	}

	private isCategoryPreSelected(
		options: CategorizationOption[],
		categoryProps: string | undefined,
	) {
		return options.some((c) => c.name === categoryProps);
	}

	private categoryFromProps(
		isCategoryPreSelected: boolean,
		categoryProps: string | undefined,
		defaultCategory: string,
	) {
		return isCategoryPreSelected
			? categoryProps || defaultCategory
			: defaultCategory;
	}

	private showCategoryDropdown(isCategoryPreSelected: boolean) {
		return !isCategoryPreSelected;
	}

	private onInputChange(
		event: ChangeEvent<
			HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
		>,
	) {
		const { target } = event;
		const { name, value } = target;

		if (name === "subject") {
			this.setState({ subject: value });
			return;
		}

		if (name === "body") {
			this.setState({ body: value });
			return;
		}

		if (name === "category") {
			this.setState({ category: value });
			return;
		}

		if (name === "ssn") {
			this.setState({ ssn: value });
		}
	}

	private changeCategory(event: ChangeEvent<HTMLSelectElement>) {
		this.onInputChange(event);
		this.setState({ ssn: "" });
	}

	private onSendMessageClick() {
		const { subject, body, category, attachments, categorizationOptions, ssn } =
			this.state;
		this.setState({ busy: true });

		const selected = categorizationOptions.find(
			(option) => option.name === category,
		);
		const context = selected?.context || "";
		const categories = selected?.categories || [];

		let sendBody = body;
		if (this.showSSNField()) {
			sendBody = `${this.t("SSNBody")}: ${ssn}\n${body}`;
		}

		return startConversation(
			this.props.customer,
			context,
			categories,
			subject,
			sendBody,
			attachments,
		)
			.then(() => this.messageSent())
			.catch((error: { status?: number }) => {
				this.messageFailed(error);
			});
	}

	private onCancelClick() {
		this.setState({ busy: true });
		window.dispatchEvent(new Event(events.newMessageSentOrCancelled));
		window.dispatchEvent(new Event(events.newMessageCancelled));
	}

	private showInfoForCategoryDenmark() {
		if (this.props.cultureCode !== culture.danish) return false;
		const { category } = this.state;
		return (
			category === messageCategory.MotorClaims ||
			category === messageCategory.PersonClaims ||
			category === messageCategory.PropertyClaims ||
			category === messageCategory.TravelClaims
		);
	}

	private showInfoForExistingClaimsSwedenAndNorway() {
		if (
			this.props.cultureCode !== culture.swedish &&
			this.props.cultureCode !== culture.norwegian
		)
			return false;
		const { category } = this.state;
		return category === messageCategory.ExistingClaim;
	}

	private messageSent() {
		this.setState({ busy: true });
		window.dispatchEvent(new Event(events.newMessageSent));
	}

	private messageFailed(error: { status?: number }) {
		this.setState({ busy: true });

		if (error.status === 401) {
			window.dispatchEvent(new Event(events.unauthorized));
		}

		window.dispatchEvent(new Event(events.newMessageFailed));
	}

	private messageBodyIsValid() {
		const { body } = this.state;
		return body.trim() !== "";
	}

	private messageSubjectIsValid() {
		const { subject } = this.state;
		return subject.trim() !== "";
	}

	private categoryIsValid() {
		const { category } = this.state;
		return category && category !== selectMessageCategoryPrompt;
	}

	private showSSNField() {
		return showSSNField(this.props.cultureCode, this.state.category);
	}

	private sendDisabled() {
		const { sendAllowed, cultureCode } = this.props;
		const { busy, category, ssn } = this.state;

		return (
			this.showInfoForExistingClaimsSwedenAndNorway() ||
			!this.categoryIsValid() ||
			!sendAllowed ||
			busy ||
			!this.messageBodyIsValid() ||
			!this.messageSubjectIsValid() ||
			(showSSNField(cultureCode, category) && !ssnIsValid(cultureCode, ssn)) ||
			this.hasActiveAttachmentUploads()
		);
	}

	private updateUploads(uploads: UploadItem[]) {
		this.setState({ uploads });
	}

	private updateAttachments(attachments: unknown[]) {
		this.setState({ attachments });
	}

	private hasActiveAttachmentUploads() {
		const { uploads } = this.state;

		return uploads.some(
			(uploadObj) =>
				!uploadObj.uploadCompleted &&
				!uploadObj.uploadFailed &&
				!uploadObj.uploadCancelled,
		);
	}

	private attachmentsDisabled() {
		const { customer } = this.props;
		return !!customer.isInternalUser;
	}

	private hideAttachments(category: string) {
		return (
			this.showInfoForExistingClaimsSwedenAndNorway() ||
			hideAttachments(category)
		);
	}

	private submitHandler(e: SubmitEvent<HTMLFormElement>) {
		e.preventDefault();
	}

	render() {
		const { sendAllowed, cultureCode, customer } = this.props;
		const {
			ssn,
			subject,
			body,
			category,
			attachments,
			uploads,
			categorizationOptions,
			showCategoryDropdown,
			loading,
		} = this.state;

		const subjectAndBodyDisabled = !sendAllowed || !this.categoryIsValid();

		return (
			<div className="mypages-offers-new-message">
				{loading ? (
					<div className="if u-justify-center">
						<ThreeDotsLoadingSpinner />
					</div>
				) : (
					<form className="if form" onSubmit={(e) => this.submitHandler(e)}>
						<fieldset className="if">
							<div className="gb form fullwidth">
								<div className="gb form-group secure-messaging-component__required-field">
									{showCategoryDropdown && (
										<div>
											<label className="if" htmlFor="category">
												{this.t("MessageCategory")}
											</label>
											<div className="gb secure-messaging-component__select select fullwidth-down--sm">
												<select
													id="category"
													name="category"
													className="gb category fullwidth-down--sm"
													required
													aria-required="true"
													value={category}
													onChange={this.changeCategory}
												>
													<option
														key={selectMessageCategoryPrompt}
														value={selectMessageCategoryPrompt}
														disabled
													>
														{this.t(
															`MessageCategory_${selectMessageCategoryPrompt}`,
														)}
													</option>
													{categorizationOptions.map((availableCategory) => (
														<option
															key={availableCategory.name}
															value={availableCategory.name}
															hidden={dropdownCategoriesToHide.includes(
																availableCategory.name,
															)}
														>
															{this.t(
																`MessageCategory_${availableCategory.name}`,
															)}
														</option>
													))}
												</select>
											</div>
										</div>
									)}
									{this.showInfoForCategoryDenmark() && (
										<div className="categoryNotification if row u-margin-top--smallest">
											<div className="if col-10--sm">
												<span className="if text bold">
													{this.t("CategoryNotification_Title")}
												</span>{" "}
												<span className="if text meta">
													{this.t(`CategoryNotification_${category}`)}{" "}
													<a href={this.l(`ReportClaim_${category}`)}>
														{this.t(`CategoryNotificationLinkText_${category}`)}
													</a>
												</span>
											</div>
										</div>
									)}
								</div>
								{this.showInfoForExistingClaimsSwedenAndNorway() && (
									<div className="if alert-banner warning u-margin-top--smallest secure-messaging-component__banner">
										{this.t("CategoryNotification_ExistingClaim_Banner")}
										<a
											href={this.l(`ReportClaim_${category}_BannerLinkText`)}
											className="if"
										>
											{this.t(
												"CategoryNotification_ExistingClaim_BannerLinkText",
											)}
											.
										</a>
										<div>
											<a
												href={this.l(`ReportClaim_${category}`)}
												className="secure-messaging-component__link-paragraph"
											>
												{this.t(`CategoryNotificationLinkText_${category}`)}
												<div className="secure-messaging-component__icon-position">
													<span
														aria-hidden="true"
														className="if icon ui arrow-right blue secure-messaging-component__icon"
													/>
												</div>
											</a>
										</div>
									</div>
								)}
								{this.showSSNField() && (
									<SSNField
										cultureCode={cultureCode}
										ssn={ssn}
										onInputChange={this.onInputChange}
									/>
								)}
								{!this.showInfoForExistingClaimsSwedenAndNorway() && (
									<div>
										<div className="gb form-group secure-messaging-component__required-field">
											<label className="if" htmlFor="subject">
												{this.t("MessageSubject")}
											</label>
											<input
												id="subject"
												name="subject"
												className="gb fullwidth"
												type="text"
												onChange={this.onInputChange}
												disabled={subjectAndBodyDisabled}
												placeholder={this.t("MessageSubjectPlaceholder")}
												aria-invalid={!this.messageSubjectIsValid()}
												aria-required="true"
												value={subject}
												maxLength={70}
											/>
										</div>
										<div className="gb form-group secure-messaging-component__required-field">
											<label className="if" htmlFor="body">
												{this.t("MessageBody")}
											</label>
											<textarea
												id="body"
												name="body"
												className="gb textarea fluid secure-messaging-component__textarea"
												rows={4}
												value={body}
												onChange={this.onInputChange}
												disabled={subjectAndBodyDisabled}
												placeholder={this.t("MessageBodyPlaceholder")}
												aria-invalid={!this.messageBodyIsValid()}
												aria-required="true"
												maxLength={10000}
											/>
										</div>
									</div>
								)}
							</div>
						</fieldset>
						{!this.hideAttachments(category) && (
							<FileAttachmentDropDown
								cultureCode={cultureCode}
								customer={customer}
								attachments={attachments}
								uploads={uploads}
								attachmentsDisabled={this.attachmentsDisabled()}
								updateUploads={this.updateUploads}
								updateAttachments={this.updateAttachments}
							/>
						)}
						<div className="if u-margin-top--40">
							<button
								id="send"
								type="button"
								onClick={this.onSendMessageClick}
								disabled={this.sendDisabled()}
								className="if button primary"
							>
								{this.t("SendMessage")}
							</button>
							<button
								id="cancel"
								type="button"
								onClick={this.onCancelClick}
								className="if button tertiary"
							>
								{this.t("CancelSending")}
							</button>
						</div>
					</form>
				)}
			</div>
		);
	}
}

export default StartConversation;
